import React, { useState, useEffect, useCallback } from "react";
import "./AccessManagement.css";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaRegUser } from "react-icons/fa";
import { useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { loginRequest } from "../../authConfig";

/**
 * Access Management Page
 * All operations (list / add / remove) go through the backend which uses
 * client-credentials (application permission) to call Microsoft Graph.
 * The frontend sends its SSO idToken for authentication.
 */
const AccessManagement = () => {
  const { instance, accounts } = useMsal();
  const baseUrl = useSelector((state) => state.url);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [email, setEmail] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const startSyncAndReload = (msg) => {
    setSyncMsg(msg);
    setSyncing(true);
    setTimeout(() => {
      window.location.reload();
    }, 20000);
  };

  // ── Get auth headers (idToken for backend verification) ────────────────────
  const getAuthHeaders = useCallback(async () => {
    if (!accounts || accounts.length === 0) return null;
    try {
      const resp = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return {
        Authorization: `Bearer ${resp.idToken}`,
        "Content-Type": "application/json",
      };
    } catch {
      instance.acquireTokenRedirect({ ...loginRequest, account: accounts[0] });
      return null;
    }
  }, [instance, accounts]);

  // ── LIST: GET /api/v1/admin/group-members ──────────────────────────────────
  const fetchGroupMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${baseUrl}/api/v1/admin/group-members`, {
        headers,
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setUsers(data.members);
    } catch (err) {
      console.error("[AccessManagement] fetch error:", err);
      setError(err.message || "Failed to load group members");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, baseUrl]);

  useEffect(() => {
    fetchGroupMembers();
  }, [fetchGroupMembers]);

  // ── Modal handlers ─────────────────────────────────────────────────────────
  const handleOpenModal = () => {
    setActionError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEmail("");
    setActionError(null);
  };

  // ── ADD: POST /api/v1/admin/group-members  { email } ──────────────────────
  const handleConfirm = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${baseUrl}/api/v1/admin/group-members`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(
          data.message || `Failed to add user (HTTP ${res.status})`,
        );
      }

      handleCloseModal();
      startSyncAndReload(`${email.trim()} has been added successfully`);
    } catch (err) {
      console.error("[AccessManagement] add error:", err);
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveClick = (user) => {
    setActionError(null);
    setUserToRemove(user);
    setShowRemoveModal(true);
  };

  const handleCloseRemoveModal = () => {
    setShowRemoveModal(false);
    setUserToRemove(null);
    setActionError(null);
  };

  // ── REMOVE: POST /api/v1/admin/group-members/remove  { userId } ──────────
  const handleConfirmRemove = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${baseUrl}/api/v1/admin/group-members/remove`, {
        method: "POST",
        headers,
        body: JSON.stringify({ userId: userToRemove.id }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(
          data.message || `Failed to remove user (HTTP ${res.status})`,
        );
      }

      handleCloseRemoveModal();
      startSyncAndReload(
        `${userToRemove.displayName} has been removed successfully`,
      );
    } catch (err) {
      console.error("[AccessManagement] remove error:", err);
      setActionError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="access-management-page">
      <div className="header-section">
        <div className="header-content">
          <div>
            <h1>Admin Panel - Access Management</h1>
            <p className="subtitle">Manage and review all access</p>
          </div>
          <button className="add-user-btn" onClick={handleOpenModal}>
            <FaRegUser /> Add User
          </button>
        </div>
      </div>

      {/* Full-screen syncing overlay */}
      {syncing && (
        <div className="sync-overlay">
          <div className="sync-overlay-box">
            <div className="sync-overlay-spinner"></div>
            <p className="sync-overlay-title">Syncing with Azure AD</p>
            <p className="sync-overlay-msg">{syncMsg}</p>
            <p className="sync-overlay-sub">
              Please wait while changes propagate…
            </p>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showModal && (
        <>
          <div
            className="custom-modal-overlay"
            onClick={handleCloseModal}
          ></div>
          <div className="custom-modal">
            <div className="custom-modal-header">
              <h3 className="custom-modal-title">Add User</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                &times;
              </button>
            </div>
            <div className="custom-modal-body">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  disabled={actionLoading}
                />
              </div>
              {actionError && <p className="modal-error">⚠ {actionError}</p>}
            </div>
            <div className="custom-modal-footer">
              <button
                className="cancel-btn"
                onClick={handleCloseModal}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirm}
                disabled={!email.trim() || actionLoading}
              >
                {actionLoading ? "Adding…" : "Confirm"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Users Table */}
      <div className="table-section">
        <div className="users-table-container">
          {/* Loading */}
          {loading && (
            <div className="table-status">
              <div className="table-spinner"></div>
              <span>Loading group members…</span>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="table-status table-error">
              <span>⚠ {error}</span>
              <button className="retry-btn" onClick={fetchGroupMembers}>
                Retry
              </button>
            </div>
          )}

          {/* Table */}
          {!loading && !error && (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="empty-row">
                      No members found in this group.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.displayName}</td>
                      <td>{user.mail}</td>
                      <td>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveClick(user)}
                        >
                          <RiDeleteBin6Line /> Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Remove Confirmation Modal */}
      {showRemoveModal && (
        <>
          <div
            className="custom-modal-overlay"
            onClick={handleCloseRemoveModal}
          ></div>
          <div className="custom-modal">
            <div className="custom-modal-header">
              <h3 className="custom-modal-title">Confirm Removal</h3>
              <button className="close-btn" onClick={handleCloseRemoveModal}>
                &times;
              </button>
            </div>
            <div className="custom-modal-body">
              <p>
                Are you sure you want to remove access for{" "}
                <strong>{userToRemove?.displayName}</strong> (
                {userToRemove?.mail})?
              </p>
              <p className="warning-text">This action cannot be undone.</p>
              {actionError && <p className="modal-error">⚠ {actionError}</p>}
            </div>
            <div className="custom-modal-footer">
              <button
                className="cancel-btn"
                onClick={handleCloseRemoveModal}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="remove-confirm-btn"
                onClick={handleConfirmRemove}
                disabled={actionLoading}
              >
                {actionLoading ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccessManagement;
